import React, { FC, ReactElement, FocusEvent, useState, useEffect, useContext } from 'react'
import { useTranslations } from 'next-intl'
import { CallOrSmsButton } from '../CallOrSmsButton/CallOrSmsButton'
import { CodeInputField } from '../CodeInputField/CodeInputField'
import { useTimeout } from '../../useTimeout'
import { SendCodeSingleButton } from '../SendCodeSingleButton/SendCodeSingleButton'
import {
  CALL_NUMBER_LABEL,
  CODE_CALL_BUTTON_DEFAULT_TEXT,
  GET_CODE_AGAIN_TEXT,
  GET_CODE_AGAIN_BY_CALL_AND_SMS_TEXT,
  SMS_CODE_INPUT_LABEL,
  VERIFY_WITH_CODE_TEXT,
} from '../../constants'
import { Languages } from '../../../../interfaces/common'
import { LocaleContext } from '../../../LocaleContext/LocaleContext'
import style from './CallAndSmsCodeField.module.css'
import { sendGATagEvent } from '../../../../utils/sendGATagEvent'

type CallAndSmsCodeFieldProps = {
  name?: string
  className?: string
  error?: ReactElement | string | null
  disabled: boolean
  isCallCode: boolean
  value: string
  eventGroup?: string
  shortForm?: boolean
  onChange?(data: FocusEvent<HTMLInputElement, Element>): void
  onClick(success: () => void, fail?: () => void, isCall?: boolean): void
  onFocus?(data: FocusEvent<HTMLInputElement, Element>): void
  onBlur?(data: FocusEvent<HTMLInputElement, Element>): void
}

export const CallAndSmsCodeField: FC<CallAndSmsCodeFieldProps> = ({
  className,
  error,
  disabled,
  isCallCode,
  value,
  name,
  eventGroup,
  shortForm,
  onChange,
  onClick,
  onFocus,
  onBlur,
}) => {
  const [isFirstSend, isFirstSendChange] = useState<boolean>(true)
  const { timeout, timeoutFinished, timeoutStart } = useTimeout()
  const t = useTranslations()
  const { locale } = useContext(LocaleContext)

  const getCodeAgainText = t(GET_CODE_AGAIN_TEXT)
  const getCodeAgainByCallAndSmsText = t(GET_CODE_AGAIN_BY_CALL_AND_SMS_TEXT)
  const codeCallText = t(CODE_CALL_BUTTON_DEFAULT_TEXT)
  const formattedTimeout = timeout < 10 ? `0${timeout}` : `${timeout}`
  const codeAgainText = locale === Languages.RU ? getCodeAgainByCallAndSmsText : getCodeAgainText
  const timeoutText = `${codeAgainText} 0:${formattedTimeout}`
  const callNumberLabel = t(CALL_NUMBER_LABEL)
  const placeholder = t(VERIFY_WITH_CODE_TEXT)
  const smsCodeLabel = t(SMS_CODE_INPUT_LABEL)

  useEffect(() => {
    if (timeoutFinished) {
      isFirstSendChange(false)
    }
  }, [timeoutFinished])

  const onCallMouseDownHandler = () => {
    if (isFirstSend && shortForm) {
      sendGATagEvent('event', 'call', {
        event_category: eventGroup,
      })
    } else if (shortForm) {
      sendGATagEvent('event', 'CallRepeat', {
        event_category: eventGroup,
      })
    }

    onClick(timeoutStart, () => isFirstSendChange(false), true)
  }

  const onSmsMouseDownHandler = () => {
    sendGATagEvent('event', 'SMS', {
      event_category: eventGroup,
    })

    onClick(timeoutStart)
  }

  return (
    <>
      {!disabled && isCallCode ? <label className={style.label}>{callNumberLabel}</label> : null}
      {!disabled && !isCallCode ? <label className={style.label}>{smsCodeLabel}</label> : null}
      <CodeInputField
        name={name}
        className={className}
        disabled={disabled}
        error={error}
        value={value}
        placeholder={placeholder}
        isCallCode={isCallCode}
        onChange={onChange}
        onFocus={onFocus}
        onBlur={onBlur}
      />
      {isFirstSend && !timeout && (
        <SendCodeSingleButton text={codeCallText} timeout={timeout} onMouseDown={onCallMouseDownHandler} />
      )}
      {!isFirstSend && (
        <CallOrSmsButton
          timeout={timeout}
          onCallMouseDownHandler={onCallMouseDownHandler}
          onSmsMouseDownHandler={onSmsMouseDownHandler}
        />
      )}
      {Boolean(timeout) && <div className={style.timeout}>{timeoutText}</div>}
    </>
  )
}

CallAndSmsCodeField.displayName = 'CallAndSmsCodeField'
